import { useEffect, useRef } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { ListFilter } from 'lucide-react';

import { useIsMobile } from '../../hooks/useIsMobile';
import {
  TASK_TYPE_FILTER_TABS,
  type TaskTypeFilterTab,
  type TaskTypeFilterValue,
} from './taskTypeFilters';

interface TaskTypeFilterProps {
  value: TaskTypeFilterValue;
  onChange: (value: TaskTypeFilterValue) => void;
  variant?: 'compact' | 'inline' | 'combobox';
  totalCount?: number;
  filteredCount?: number;
  className?: string;
  tabs?: ReadonlyArray<TaskTypeFilterTab>;
}

const TASK_FILTER_TAB_LIST_CLASSES_8 =
  'grid h-auto min-h-8 w-full grid-cols-2 gap-0.5 rounded-md border border-border bg-muted/40 p-1 min-[420px]:grid-cols-4 lg:grid-cols-8';

const TASK_FILTER_TAB_LIST_CLASSES_10 =
  'grid h-auto min-h-8 w-full grid-cols-2 gap-0.5 rounded-md border border-border bg-muted/40 p-1 min-[420px]:grid-cols-5 lg:grid-cols-10';

const TASK_FILTER_TAB_LIST_CLASSES_11 =
  'grid h-auto min-h-8 w-full grid-cols-2 gap-0.5 rounded-md border border-border bg-muted/40 p-1 min-[420px]:grid-cols-6 lg:grid-cols-11';

const TASK_FILTER_TAB_LIST_CLASSES_7 =
  'grid h-auto min-h-8 w-full grid-cols-2 gap-0.5 rounded-md border border-border bg-muted/40 p-1 min-[420px]:grid-cols-4 lg:grid-cols-7';

const TASK_FILTER_TAB_CLASSES =
  'min-h-8 whitespace-normal px-1.5 py-1.5 text-[11px] font-medium leading-tight [overflow-wrap:anywhere] data-[state=active]:shadow-none sm:text-xs';

const TASK_FILTER_COMBOBOX_TAB_CLASSES =
  'min-h-7 whitespace-normal px-1 py-1 text-[11px] font-medium leading-tight [overflow-wrap:anywhere] data-[state=active]:shadow-none sm:text-xs';

export const TaskTypeFilter = ({
  value,
  onChange,
  variant = 'compact',
  totalCount,
  filteredCount,
  className,
  tabs = TASK_TYPE_FILTER_TABS,
}: TaskTypeFilterProps) => {
  const isMobile = useIsMobile();
  const pendingValueRef = useRef<TaskTypeFilterValue | null>(null);
  const pendingResetRef = useRef<number | null>(null);
  const countText =
    typeof filteredCount === 'number' && typeof totalCount === 'number'
      ? `Showing ${filteredCount} of ${totalCount} rows`
      : null;
  const containerClass =
    variant === 'combobox'
      ? 'border-b border-border bg-background px-2 py-2'
      : variant === 'inline'
        ? 'w-full border-b border-border/60 px-3 py-2 max-lg:px-2 max-lg:py-1.5'
        : 'border-b border-border bg-muted/20 p-3';
  const tabListClass =
    tabs.length <= 7
      ? TASK_FILTER_TAB_LIST_CLASSES_7
      : tabs.length <= 8
        ? TASK_FILTER_TAB_LIST_CLASSES_8
        : tabs.length <= 10
          ? TASK_FILTER_TAB_LIST_CLASSES_10
          : TASK_FILTER_TAB_LIST_CLASSES_11;
  const tabClass =
    variant === 'combobox'
      ? TASK_FILTER_COMBOBOX_TAB_CLASSES
      : TASK_FILTER_TAB_CLASSES;
  const handleValueChange = (nextValue: string) => {
    const nextFilter = nextValue as TaskTypeFilterValue;
    if (pendingValueRef.current === nextFilter) {
      return;
    }

    pendingValueRef.current = nextFilter;
    if (pendingResetRef.current !== null) {
      window.clearTimeout(pendingResetRef.current);
    }
    pendingResetRef.current = window.setTimeout(() => {
      pendingValueRef.current = null;
      pendingResetRef.current = null;
    }, 0);
    onChange(nextFilter);
  };

  useEffect(() => {
    return () => {
      if (pendingResetRef.current !== null) {
        window.clearTimeout(pendingResetRef.current);
      }
    };
  }, []);

  const filterControl = isMobile ? (
    <Select value={value} onValueChange={handleValueChange}>
      <SelectTrigger
        selectSize='sm'
        className='w-full'
        aria-label='Task type filters'
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className='w-[var(--radix-select-trigger-width)]'>
        {tabs.map(tab => (
          <SelectItem key={tab.value} value={tab.value} title={tab.tooltip}>
            {tab.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  ) : (
    <Tabs
      value={value}
      onValueChange={handleValueChange}
      variant='purple'
      className='min-w-0 flex-1'
    >
      <TabsList
        aria-label='Task type filters'
        className={tabListClass}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.stopPropagation();
          }
        }}
      >
        <TooltipProvider delayDuration={300}>
          {tabs.map(tab => (
            <Tooltip key={tab.value}>
              <TooltipTrigger asChild>
                <span className='min-w-0'>
                  <TabsTrigger
                    value={tab.value}
                    className={`${tabClass} w-full`}
                    title={tab.tooltip}
                  >
                    {tab.label}
                  </TabsTrigger>
                </span>
              </TooltipTrigger>
              <TooltipContent side='top'>
                <p>{tab.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          ))}
        </TooltipProvider>
      </TabsList>
    </Tabs>
  );

  return (
    <div className={containerClass + (className ? ` ${className}` : '')}>
      <div className='flex w-full flex-col gap-2 lg:flex-row lg:items-center lg:justify-between'>
        <div className='flex min-w-0 flex-1 items-center gap-2'>
          <ListFilter
            className='h-4 w-4 shrink-0 text-muted-foreground max-lg:hidden'
            aria-hidden='true'
          />
          {filterControl}
        </div>
        {countText ? (
          <div className='shrink-0 text-xs text-muted-foreground lg:text-right max-lg:hidden'>
            {countText}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TaskTypeFilter;
